import React, { useState } from 'react';
import { styled } from 'linaria/react';
import { cx } from 'linaria';
import t from '@jetshop/intl';
import { theme } from '../Theme';
import useFormCustomInfo from './useFormCustomInfo';
import Accordion from '@jetshop/ui/Accordion/Accordion';
import { ReactComponent as Plus } from '../../svg/plus.svg';
import { Above, Below } from '@jetshop/ui/Breakpoints';
import striptags from 'striptags';
import { useAppSettingsStore } from '../../appSettingsStore';
import { render } from 'storyblok-rich-text-react-renderer';

const DescriptionText = styled('article')`
  margin-bottom: 3.25rem;
  color: ${theme.colors.text};
  font-size: 13px;
  .read-more {
    margin-top: -3rem;
    padding-top: 3rem;
    position: relative;
    button {
      text-decoration: underline;
      color: ${theme.colors.tretornBlue};
      font-weight: 700;
    }
  }
  .read-more.closed {
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 1) 60%
    );
  }
  button {
    background: none;
    display: inline-block;
    font-weight: 600;

    &.open {
      display: block;
    }

    .open_description {
      max-height: 200px;
      overflow: hidden;
    }

    ${theme.below.md} {
      margin-bottom: 2rem;
      font-size: 12px;
    }
  }
`;

const StyledPlus = styled('div')`
  svg {
    transition: transform 0.3s ease;
    transform: rotate(${props => (props.isOpen ? '45deg' : '0deg')});
  }
`;

export const SectionHeading = styled('h2')`
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
`;

export const ProductSection = styled('section')`
  line-height: 1.5;

  width: 100%;

  ${theme.below.md} {
    margin: 0;
    padding-bottom: 1.5rem;
  }

  ${theme.above.md} {
    width: 50%;
  }

  ${theme.above.lg} {
    :nth-of-type(odd) {
      width: 66.6667%;
    }
    :nth-of-type(even) {
      width: 33.3333%;
    }
  }
  ul.payment-options-list {
    font-size: 16px;
    font-weight: 600;
  }
`;

const StyledAccordion = styled('div')`
  ${theme.below.md} {
    border-bottom: 1px solid ${theme.colors.lightGrey};
    &:first-of-type {
      border-top: 1px solid ${theme.colors.lightGrey};
    }
  }
  .accordion-title {
    padding-bottom: 8px;
    font-size: 13px;
    line-height: 19.5px;
    text-transform: capitalize;
    font-weight: 400;
    letter-spacing: -0.02em;
    color: ${theme.colors.black};
    cursor: pointer;

    display: flex;
    gap: 8px;
    align-items: center;
    ${theme.below.md} {
      justify-content: space-between;
      padding: 24px 0;
      font-size: 16px;
    }

    svg {
      width: 12px;
      ${theme.below.md} {
        width: 16px;
      }
    }

    ~ div > *:last-of-type {
      padding-bottom: 0.75rem;
    }
  }
`;

const StyledList = styled('ul')`
  font-size: 12px;
  color: ${theme.colors.text};
  li {
    display: flex;
    margin-bottom: 1rem;
  }
  .column-left {
    font-weight: 600;
    font-size: 13px;
    width: 9rem;
    color: ${theme.colors.text};
    text-transform: capitalize;
    flex-shrink: 0;
  }
  .column-right {
    margin-left: 1rem;
    color: ${theme.colors.text};
  }

  &.description-list {
    margin-bottom: 1.875rem;
  }
`;

const StyledText = styled('p')`
  color: ${theme.colors.text};
  font-size: 0.865rem;
  line-height: 1.57;
`;

const Description = ({ description, isMobile }) => {
  const [open, setOpen] = useState(false);
  const strippedDescription = striptags(description);
  const showMore = isMobile && strippedDescription.length > 300;

  return (
    <DescriptionText>
      <div
        dangerouslySetInnerHTML={{ __html: description }}
        className={cx(
          showMore && !open ? 'open_description' : 'closed_description'
        )}
      />
      {isMobile && strippedDescription.length > 300 && (
        <div className={cx('read-more', open ? 'open' : 'closed')}>
          <button className={open ? 'open' : ''} onClick={() => setOpen(!open)}>
            {open ? t('Show less') : t('Read more')}
          </button>
        </div>
      )}
    </DescriptionText>
  );
};

const ProductInfo = ({ className, product, openReviews, setopenReviews }) => {
  const { descriptionList, specificationList } = useFormCustomInfo(product);

  return (
    <>
      {product.description && (
        <div className={className}>
          <AccordionComponent
            title={t('product_information')}
            Component={InformationContent}
            componentProps={{ product, descriptionList }}
          />
          <ProductCustomInfo
            specificationList={specificationList}
            openReviews={openReviews}
            setopenReviews={setopenReviews}
          />
        </div>
      )}
    </>
  );
};

const ProductCustomInfo = ({
  specificationList,
  descriptionList,
  product,
  openReviews,
  setopenReviews
}) => {
  const shippingAndReturns = useAppSettingsStore(
    state => state.shippingAndReturns
  );
  return (
    <>
      {specificationList.length > 0 && (
        <AccordionComponent
          title={t('Specifications')}
          Component={List}
          componentProps={{ list: specificationList }}
        />
      )}
      <AccordionComponent
        title={t('Shipping & Return')}
        Component={Text}
        componentProps={{
          text: <div>{render(shippingAndReturns)}</div>
        }}
      />
      <AccordionComponent
        openReviews={openReviews}
        setopenReviews={setopenReviews}
        title={t('reviews')}
        Component={ReviewsContent}
        componentProps={{}}
      />
    </>
  );
};

const AccordionComponent = ({
  title,
  Component,
  componentProps,
  openReviews,
  setopenReviews
}) => (
  <Accordion single>
    {({ openIndexes, handleClick, AccordionContent }) => {
      if (openReviews && !openIndexes.includes(0)) {
        handleClick(0);
      }
      const handleTitleClick = () => {
        handleClick(0);
        setopenReviews(false); // Setting openReviews to false
      };
      return (
        <StyledAccordion>
          <div className="accordion-title" onClick={handleTitleClick}>
            <Above breakpoint="md">
              <StyledPlus isOpen={openIndexes.includes(0)}>
                <Plus />
              </StyledPlus>
            </Above>
            {title}
            <Below breakpoint="md">
              <StyledPlus isOpen={openIndexes.includes(0)}>
                <Plus />
              </StyledPlus>
            </Below>
          </div>
          <AccordionContent isOpen={openIndexes.includes(0)}>
            <Component {...componentProps} />
          </AccordionContent>
        </StyledAccordion>
      );
    }}
  </Accordion>
);

const ReviewsContent = () => (
  <div>
    <div id="testfreaks-reviews" />
    <div id="testfreaks-qa" />
    <div id="testfreaks-videos" />
  </div>
);

const InformationContent = ({ product, descriptionList }) => (
  <>
    <Above breakpoint="md">
      {matches => (
        <Description description={product.description} isMobile={!matches} />
      )}
    </Above>
    <List list={descriptionList} className="description-list" />
  </>
);

const List = ({ list, ...props }) => (
  <StyledList {...props}>
    {list.map(({ name, value }) => {
      const valueWithLineBreaks = value.replace(/\s•/g, '<br>•');
      return (
        <li key={name}>
          <span className="column-left">{name}</span>
          <span
            className="column-right"
            dangerouslySetInnerHTML={{ __html: valueWithLineBreaks }}
          />
        </li>
      );
    })}
  </StyledList>
);

const Text = ({ text }) => <StyledText>{text}</StyledText>;

export default ProductInfo;
